<template>
    <div>
        <div class="setting-header">Kravia</div>
        <div v-if="settingsLoaded">
            <div v-if="!onboardingCompleted && !kraviaCreditorAlreadyExists" class="my-2 flex items-center gap-2">
                <span class="mb-">Kravia er ikke aktivert </span>
                <i class="i-ph-x text-red-500 text-xl"/>
            </div>
            <div v-else class="my-2 flex items-center gap-2">
                <span class="mb-">Kravia er aktivert </span>
                <i class="i-ph-check text-green-500 text-2xl"/>
            </div>
            <div v-if="!onboardingCompleted && !kraviaCreditorAlreadyExists && !onboardingInitiated" class="setting-group">
                <div class="setting">
                    <div class=" flex justify-between">
                        <div>Integrer med Kravia for å sende purring og inkassovarsel automatisk</div>
                        <UButton @click="onboardingInitiated = true">Initier onboarding</UButton>
                    </div>
                </div>
            </div>
            <div v-if="(onboardingCompleted || kraviaCreditorAlreadyExists) || onboardingInitiated">
                <div class="setting-header mt-6 mb-2">
                    {{ (kraviaCreditorAlreadyExists || onboardingCompleted) ? 'Innstillinger' : 'Onboarding' }}
                </div>
                <div class="setting-group">
                    <div class="setting">
                        <div class="flex justify-between items-center">
                            <div class="flex items-center gap-1">
                                <span>{{ t(`setting.${kraviaFirstReminderTypeSetting?.name}.title`) }}</span>
                                <UTooltip
                                    :text="firstReminderHelpText"
                                    :delay-duration="0"
                                    :ui="{
                                        content: 'h-fit overflow-visible',
                                        text: 'whitespace-pre-line p-1',
                                    }"
                                >
                                    <i class="i-ph-info inline-block" />
                                </UTooltip>
                            </div>

                            <div class="w-[200px]">
                                <SimpleSelect
                                    v-model="selectedFirstReminder"
                                    :options="reminderOptions"
                                    placeholder="Velg første påminnelse"
                                    label="name"
                                    track-by="value"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="setting with-border">
                        <Setting name="KRAVIA_FIRST_REMINDER_WHEN"/>
                    </div>
                    <div v-if="!onboardingCompleted && !kraviaCreditorAlreadyExists" class="setting with-border">
                        <Setting name="KRAVIA_COLLECTION_MINIMUM_AMOUNT"/>
                    </div>
                    <div v-if="!onboardingCompleted && !kraviaCreditorAlreadyExists" class="setting with-border border-gray-300/50 dark:border-gray-400/30 border-b-2">
                        <Setting name="KRAVIA_BANK_ACCOUNT_NUMBER" is-bank-account/>
                    </div>
                    <div class="setting with-border">
                        <div class="py-4 flex justify-between">
                            <span class="cursor-default">{{ t(`setting.${kraviaInvitedEmailsSetting?.name}.title`) }}</span>
                            <div class="flex flex-col justify-between items-end">
                                <div v-for="(input, index) in emailInputs" :key="index" class="mb-2 flex items-center gap-2">
                                    <span v-if="index == 0 && !onboardingCompleted && !kraviaCreditorAlreadyExists" class="text-gray-400">Hovedkontaktperson</span>
                                    <UButton
                                        v-if="!input.invited && onboardingCompleted"
                                        variant="subtle"
                                        color="neutral"
                                        class="w-[34px] flex justify-center"
                                        @click="removeInput(index)"
                                    >
                                        -
                                    </UButton>

                                    <div class="flex flex-row w-[400px">
                                        <general-input
                                            v-model="emailInputs[index].email"
                                            :disabled="input.invited"
                                            type="email"
                                            placeholder="Mailadresse"
                                            class="w-[330px]"
                                            icon="i-ph-envelope-simple-fill"
                                            :is-valid="subAccountValidator.validateBlankOrEmail"
                                            @blur="onBlurEmailInput"
                                        />

                                        <UButton
                                            v-if="onboardingCompleted || kraviaCreditorAlreadyExists"
                                            :disabled="input.invited || emailAlreadyInvited(input.email) || input.beingSent"
                                            variant="outline"
                                            color="neutral"
                                            @click="sendEmailInvitation(input)"
                                        >
                                            <span v-if="input.beingSent" class="loading loading-spinner loading-md"/>
                                            <div v-else>
                                                {{ input.invited ? 'Invitert' : 'Inviter' }}
                                            </div>
                                        </UButton>
                                    </div>
                                </div>
                                <UButton
                                    v-if="onboardingCompleted || kraviaCreditorAlreadyExists"
                                    variant="subtle"
                                    color="neutral"
                                    class="flex justify-center w-[34px]"
                                    @click="addInput"
                                >
                                    +
                                </UButton>
                            </div>
                        </div>
                    </div>
                    <div class="setting with-border">
                        <Setting name="KRAVIA_BANK_LEDGER_ACCOUNT" is-account/>
                    </div>

                    <div class="setting with-border">
                        <Setting name="KRAVIA_INTEREST_FEE_ACCOUNT" is-account/>
                    </div>

                    <div class="setting with-border">
                        <Setting name="KRAVIA_FEES_PAID_IN_ADVANCE_ACCOUNT" is-account/>
                    </div>

                    <div class="setting with-border">
                        <Setting name="KRAVIA_COLLECTION_AGENCY_ACCOUNT" is-account/>
                    </div>

                    <div class="setting flex justify-end" :title="resolveOnboardingButtonLabel">
                        <UButton
                            :disabled="submissionInProgress || !onboardingValid"
                            @click="onboardOrUpdateKraviaCreditor"
                        >
                            <span v-if="submissionInProgress" class="loading loading-spinner loading-md"/>
                            {{ onboardingCompleted || kraviaCreditorAlreadyExists ? 'Oppdater Kravia' : 'Integrer med Kravia' }}
                        </UButton>
                    </div>
                </div>
            </div>
            <div v-if="(onboardingCompleted || kraviaCreditorAlreadyExists) && kraviaCreditorIdSetting.value ">
                <div class="setting-header mb-2">Andre innstillinger</div>
                <div class="setting-group">
                    <div class="py-6">
                        <div>
                            Gå til
                            <a
                                class="text-primary cursor-pointer"
                                target="_blank"
                                :href="resolveCreditorUrl(kraviaCreditorIdSetting.value)"
                            > Kravia sin kundeportal
                            </a>
                            for å se/endre innstillinger som blant annet påminnelsestype, minimumsbeløp og bankkontonummer
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="flex justify-center">
            <i class="loading loading-spinner loading-lg h-28"/>
        </div>
    </div>
</template>
<script setup lang="ts">
import Setting from '@/setting/Setting.vue'
import {computed, onMounted, ref} from 'vue'
import SimpleSelect from '@/components/SimpleSelect.vue'
import {refreshSetting, setSetting, useSetting} from '@/setting/setting.service'
import GeneralInput from '@/components/GeneralInput.vue'
import {useI18n} from 'vue-i18n'
import {KraviaReminder, reminderOptions} from '@/debt-collection/KraviaReminder'
import {useSubAccountValidation} from '@/subaccount/subAccountValidator'
import {initiateOnboarding, isKraviaResponse, postEmailInvitation, updateCreditor} from '@/debt-collection/debtCollection.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SWITCHED} from '@/types/events'
import {isHttpResponse} from '@/services/infrastructure/api.service'
import {isLocal, isStaging} from '@/services/infrastructure/envhelper.service'

const {onEvent} = useEmitter()
const {t} = useI18n()
const subAccountValidator = useSubAccountValidation()

const {setting: kraviaCreditorIdSetting} = useSetting<string>('KRAVIA_CREDITOR_ID')
const {setting: kraviaFirstReminderTypeSetting} = useSetting<string>('KRAVIA_FIRST_REMINDER_TYPE')
const {setting: kraviaInvitedEmailsSetting} = useSetting<string>('KRAVIA_INVITED_EMAILS')
const {setting: kraviaBankAccountNumberSetting} = useSetting<string>('KRAVIA_BANK_ACCOUNT_NUMBER')
const invitedEmails = computed(() => kraviaInvitedEmailsSetting.value?.value ? kraviaInvitedEmailsSetting.value.value.split(',') : [])

const kraviaCreditorAlreadyExists = computed(() => kraviaCreditorIdSetting.value?.value !== null)
const onboardingCompleted = ref(false)
const onboardingInitiated = ref(false)
const submissionInProgress = ref(false)

const settingsLoaded = ref(false)

const resolveCreditorUrl = (creditorId: string) => {
    if (isStaging() || isLocal()) {
        return `https://app-qa.kravia.ai/creditor/${creditorId}`
    }
    return `https://app.kravia.ai/creditor/${creditorId}`
}

const selectedFirstReminder = computed({
    get: () => reminderOptions.find(option => option.value === kraviaFirstReminderTypeSetting.value.value) ?? reminderOptions.find(option => option.value === kraviaFirstReminderTypeSetting.value.default),
    set: (value: { name: string, value: KraviaReminder }) => {
        setSetting(kraviaFirstReminderTypeSetting.value.name, value.value)
    }
})

type EmailInput = {
    email: string,
    invited: boolean,
    beingSent: boolean
}

const firstReminderHelpText = 'Velg hvilken type påminnelse som skal sendes som første purring til kundene dine.'

const fetchInvitedEmails = async () => {
    await refreshSetting('KRAVIA_INVITED_EMAILS')
    const uninvitedEmailInputs = emailInputs.value.filter(input => !input.invited && input.email !== '')
    emailInputs.value = []
    invitedEmails.value.forEach((email) => {
        emailInputs.value.push({email: email, invited: true, beingSent: false})
    })
    uninvitedEmailInputs.forEach((input) => {
        emailInputs.value.push(input)
    })
    if (uninvitedEmailInputs.length === 0) {
        emailInputs.value.push({email: '', invited: false, beingSent: false})
    }
}
const emailInputs = ref<EmailInput[]>([{email: '', invited: false, beingSent: false}])

const addInput = () => {
    const inputs = emailInputs.value.slice()
    inputs.push({email: '', invited: false, beingSent: false})
    emailInputs.value = inputs
}

const onboardingValid = computed(() => {
    return subAccountValidator.validateBlankOrEmail(emailInputs.value[0].email) === undefined
        && kraviaBankAccountNumberSetting.value.value
})

const resolveOnboardingButtonLabel = computed(() => {
    let label = ''
    if (subAccountValidator.validateBlankOrEmail(emailInputs.value[0].email) !== undefined) {
        label += 'Hovedkontaktperson må ha en gyldig epost\n'
    }
    if (!kraviaBankAccountNumberSetting.value.value) {
        label += 'Bankkontonummer må være satt'
    }
    return label
})

const removeInput = (index: number) => {
    if (emailInputs.value.length > 1) {
        const inputs = emailInputs.value.slice()
        inputs.splice(index, 1)
        emailInputs.value = inputs
    }
}

const onBlurEmailInput = () => {
    emailInputs.value = emailInputs.value.slice()
}

const sendEmailInvitation = async (emailInput: EmailInput) => {
    emailInput.beingSent = true
    const response = await postEmailInvitation(emailInput.email)
    if (isKraviaResponse(response) && response.successful) {
        emailInput.invited = true
        await fetchInvitedEmails()
    } else if (isHttpResponse(response) && response.status >= 400) {
        emailInput.beingSent = false
        switch (response.status) {
            case 409:
                notifyError({title: 'Invitasjon feilet', description: `Eposten ${emailInput.email} er allerede invitert til Kravia.`})
                return
            case 400:
                notifyError({title: 'Invitasjon feilet', description: `Eposten ${emailInput.email} er ugyldig.`})
                return
            default:
                notifyError({title: 'Invitasjon feilet', description: 'En ukjent feil har oppstått'})
                return
        }
    }
    emailInput.beingSent = false
}

onEvent(ORG_SWITCHED, async () => {
    settingsLoaded.value = false
    onboardingInitiated.value = false
    await fetchInvitedEmails()
    settingsLoaded.value = true
})
onMounted(async () => {
    await fetchInvitedEmails()
    settingsLoaded.value = true
})

const emailAlreadyInvited = (email: string) => {
    return invitedEmails.value.includes(email)
}

const onboardOrUpdateKraviaCreditor = async () => {
    const invalid = emailInputs.value.every(emailInput => subAccountValidator.validateBlankOrEmail(emailInput.email))

    if (invalid) return
    submissionInProgress.value = true

    if (onboardingCompleted.value || kraviaCreditorAlreadyExists.value) {
        const creditorId = kraviaCreditorIdSetting.value.value
        const response = await updateCreditor(creditorId)
        if (response.successful) {
            await refreshSetting('KRAVIA_CREDITOR_ID')
            submissionInProgress.value = false
        } else {
            submissionInProgress.value = false
            notifyError({title: 'Feilet', description: {descriptionMessage: 'Klarte ikke å oppdatere Kravia-kreditor', error: response.errors}})
        }
    } else {
        if (emailInputs.value[0].email === '') {
            notifyError({title: 'Feilet', description: 'Hovedkontakt mangler.'})
        }
        const response = await initiateOnboarding(emailInputs.value[0].email)
        if (response.successful) {
            await sendEmailInvitation(emailInputs.value[0])
            await refreshSetting('KRAVIA_CREDITOR_ID')
            onboardingCompleted.value = true
            submissionInProgress.value = false
        } else {
            submissionInProgress.value = false
            notifyError({title: 'Feilet', description: {descriptionMessage: 'Klarte ikke å onboarde Kravia-kreditor', error: response.errors}})
        }
    }
}

</script>

<style scoped>

</style>
